import moment from 'moment';

export const CUSTOMER_CARE_DATE_RANGES = [
  { value: '1', label: 'Last 1 day' },
  { value: '3', label: 'Last 3 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days (all)' },
];

export const CUSTOMER_CARE_PAGE_SIZE_OPTIONS = [50, 100, 250, 500];

export const CUSTOMER_CARE_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'address-update', label: 'Address Update' },
  { value: 'email-update', label: 'Email Update' },
  { value: 'email-optin', label: 'Email Optin' },
  { value: 'unsubscribe', label: 'Unsubscribe' },
  { value: 'mobile-number-update', label: 'Mobile Number Update' },
  { value: 'ownership', label: 'Ownership' },
  { value: 'non-owner', label: 'Non-Owner' },
  { value: 'christophorus-extend', label: 'Christophorus Extend' },
  { value: 'christophorus-cancel', label: 'Christophorus Cancel' },
];

export const CUSTOMER_CARE_SUBMISSION_FIELD_DEFINITIONS = [
  { key: 'id', label: 'Record ID' },
  { key: 'customercare_type', label: 'CC Type' },
  { key: 'bpid', label: 'BP ID' },
  { key: 'dealer', label: 'Dealer' },
  { key: 'vin', label: 'VIN' },
  { key: 'qr', label: 'QR' },
  { key: 'email', label: 'Email' },
  { key: 'email_auto', label: 'Email Auto-Filled' },
  { key: 'confirm_email', label: 'Confirm Email' },
  { key: 'email_optin', label: 'Email Opt-In' },
  { key: 'mobile_phone', label: 'Mobile Phone' },
  { key: 'address_type', label: 'Address Type' },
  { key: 'company', label: 'Company' },
  { key: 'address1', label: 'Address 1' },
  { key: 'address2', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'postal', label: 'Postal' },
  { key: 'country', label: 'Country' },
  { key: 'vehicle_status', label: 'Vehicle Status' },
  { key: 'vehicle_status_month', label: 'Vehicle Status Month' },
  { key: 'vehicle_status_year', label: 'Vehicle Status Year' },
  { key: 'type', label: 'Type' },
  { key: 'importer', label: 'Importer' },
  { key: 'sap_outbound_id', label: 'SAP Outbound ID' },
  { key: 'utm_source', label: 'UTM Source' },
  { key: 'utm_medium', label: 'UTM Medium' },
  { key: 'utm_campaign', label: 'UTM Campaign' },
  { key: 'utm_term', label: 'UTM Term' },
  { key: 'utm_content', label: 'UTM Content' },
  { key: 'sentToCap', label: 'Sent to C@P' },
  { key: 'createdAt', label: 'Created' },
  { key: 'updatedAt', label: 'Updated' },
];

export const DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS = {
  search: '',
  ccType: 'all',
  sentToCap: 'all',
  dateRangeDays: '7',
  sortKey: 'createdAt',
  sortDir: 'desc',
  pageSize: 100,
  currentPage: 1,
};

const STORAGE_KEY = 'manageCustomerCareDashboard:v1';

const VALID_VALUES = {
  ccType: CUSTOMER_CARE_TYPE_OPTIONS.map((option) => option.value),
  sentToCap: ['all', 'yes', 'no'],
  dateRangeDays: CUSTOMER_CARE_DATE_RANGES.map((option) => option.value),
  sortKey: [
    'customercare_type',
    'bpid',
    'dealer',
    'vin',
    'email',
    'email_optin',
    'mobile_phone',
    'address',
    'vehicle',
    'additional',
    'createdAt',
    'sentToCap',
  ],
  sortDir: ['asc', 'desc'],
};

const cleanChoice = (value, key) => {
  const normalized = value === '' || value === undefined || value === null ? 'all' : String(value);
  if (key === 'sentToCap') {
    if (normalized.toLowerCase() === 'yes') return 'yes';
    if (normalized.toLowerCase() === 'no') return 'no';
  }
  return VALID_VALUES[key].includes(normalized)
    ? normalized
    : DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS[key];
};

const cleanPageSize = (value) => {
  const pageSize = Number(value);
  return CUSTOMER_CARE_PAGE_SIZE_OPTIONS.includes(pageSize)
    ? pageSize
    : DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS.pageSize;
};

const cleanPage = (value) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0
    ? page
    : DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS.currentPage;
};

export const normalizeCustomerCareDashboardSettings = (settings = {}) => {
  const source = settings && typeof settings === 'object' ? settings : {};

  return {
    ...DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS,
    search: typeof source.search === 'string' ? source.search : '',
    ccType: cleanChoice(source.ccType, 'ccType'),
    sentToCap: cleanChoice(source.sentToCap, 'sentToCap'),
    dateRangeDays: cleanChoice(source.dateRangeDays, 'dateRangeDays'),
    sortKey: cleanChoice(source.sortKey, 'sortKey'),
    sortDir: cleanChoice(source.sortDir, 'sortDir'),
    pageSize: cleanPageSize(source.pageSize),
    currentPage: cleanPage(source.currentPage),
  };
};

export const readCustomerCareDashboardSettings = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS;
  }

  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return normalizeCustomerCareDashboardSettings(value ? JSON.parse(value) : null);
  } catch {
    return DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS;
  }
};

export const writeCustomerCareDashboardSettings = (settings) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(normalizeCustomerCareDashboardSettings(settings)),
  );
};

export const clearCustomerCareDashboardSettings = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(STORAGE_KEY);
};

export const toSubmissionTimestamp = (value) => {
  if (!value) return null;

  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }

  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + (value.nanoseconds || 0) / 1000000;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const parsed = moment(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

export const getSubmissionCreatedTimestamp = (submission) =>
  toSubmissionTimestamp(submission?.createdAt) ||
  toSubmissionTimestamp(submission?.createdDate);

export const isCustomerCareBooleanYes = (value) =>
  value === true ||
  ['true', 'yes', '1'].includes(String(value).trim().toLowerCase());

const booleanMatches = (value, filter) => {
  if (filter === 'all') return true;
  return filter === 'yes'
    ? isCustomerCareBooleanYes(value)
    : !isCustomerCareBooleanYes(value);
};

const dateRangeMatches = (submission, dateRangeDays, now = new Date()) => {
  const created = getSubmissionCreatedTimestamp(submission);
  if (!created) return false;

  const cutoff = moment(now).subtract(Number(dateRangeDays), 'days').valueOf();
  return created >= cutoff;
};

export const formatCustomerCareBoolean = (value) => {
  if (isCustomerCareBooleanYes(value)) return 'Yes';
  if (
    value === false ||
    ['false', 'no', '0'].includes(String(value).trim().toLowerCase())
  ) {
    return 'No';
  }
  return value || '';
};

export const formatCustomerCareDate = (value) => {
  const timestamp = toSubmissionTimestamp(value);
  return timestamp ? moment(timestamp).format('MM/DD/YYYY h:mm A') : '';
};

const compactJoin = (values, separator = ' ') =>
  values.filter((value) => value !== undefined && value !== null && value !== '').join(separator);

export const formatCustomerCareAddress = (submission) => {
  const street = compactJoin([submission?.address1, submission?.address2], ', ');
  const locality = compactJoin([submission?.city, submission?.state, submission?.postal]);
  return compactJoin([street, locality], '\n');
};

export const formatCustomerCareVehicle = (submission) =>
  compactJoin([
    submission?.vehicle_status,
    compactJoin([submission?.vehicle_status_month, submission?.vehicle_status_year]),
  ], ' ');

export const formatCustomerCareAdditionalData = (submission) =>
  compactJoin([
    submission?.id,
    submission?.qr,
    submission?.type,
    submission?.importer,
    submission?.sap_outbound_id,
    submission?.utm_source,
    submission?.utm_medium,
    submission?.utm_campaign,
    submission?.utm_term,
    submission?.utm_content,
  ], ' ');

export const getCustomerCareSubmissionFieldValue = (submission, key) => {
  if (key === 'email_auto' || key === 'email_optin' || key === 'sentToCap') {
    return formatCustomerCareBoolean(submission?.[key]);
  }

  if (key === 'createdAt') {
    return formatCustomerCareDate(submission?.createdAt) || submission?.createdDate || '';
  }

  if (key === 'updatedAt') {
    return formatCustomerCareDate(submission?.updatedAt) || submission?.updatedDate || '';
  }

  return submission?.[key] || '';
};

const textMatches = (submission, search) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;

  return CUSTOMER_CARE_SUBMISSION_FIELD_DEFINITIONS.some(({ key }) =>
    String(getCustomerCareSubmissionFieldValue(submission, key) || '')
      .toLowerCase()
      .includes(query),
  );
};

export const submissionMatchesCustomerCareFilters = (
  submission,
  settings,
  now = new Date(),
) => {
  const normalized = normalizeCustomerCareDashboardSettings(settings);

  return (
    dateRangeMatches(submission, normalized.dateRangeDays, now) &&
    textMatches(submission, normalized.search) &&
    (normalized.ccType === 'all' ||
      String(submission?.customercare_type || '').toLowerCase() === normalized.ccType) &&
    booleanMatches(submission?.sentToCap, normalized.sentToCap)
  );
};

const compareValues = (a, b, dir) => {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (a < b) return -1 * dir;
  if (a > b) return 1 * dir;
  return 0;
};

const sortValue = (submission, key) => {
  if (key === 'createdAt') return getSubmissionCreatedTimestamp(submission);
  if (key === 'sentToCap') return isCustomerCareBooleanYes(submission?.sentToCap) ? 1 : 0;
  if (key === 'email_optin') return String(formatCustomerCareBoolean(submission?.email_optin)).toLowerCase();
  if (key === 'address') {
    return compactJoin([
      submission?.address_type,
      submission?.company,
      formatCustomerCareAddress(submission),
      submission?.country,
    ], ' ').toLowerCase();
  }
  if (key === 'vehicle') return formatCustomerCareVehicle(submission).toLowerCase();
  if (key === 'additional') return formatCustomerCareAdditionalData(submission).toLowerCase();
  return String(submission?.[key] || '').toLowerCase();
};

export const sortCustomerCareSubmissions = (submissions, settings) => {
  const normalized = normalizeCustomerCareDashboardSettings(settings);
  const dir = normalized.sortDir === 'asc' ? 1 : -1;

  return [...(submissions || [])].sort((a, b) => {
    const primary = compareValues(
      sortValue(a, normalized.sortKey),
      sortValue(b, normalized.sortKey),
      dir,
    );
    if (primary !== 0) return primary;

    return (
      compareValues(getSubmissionCreatedTimestamp(a), getSubmissionCreatedTimestamp(b), -1) ||
      compareValues(a?.id || '', b?.id || '', 1)
    );
  });
};

export const getFilteredCustomerCareSubmissions = (
  submissions,
  settings,
  now = new Date(),
) => {
  const normalized = normalizeCustomerCareDashboardSettings(settings);
  return sortCustomerCareSubmissions(
    (submissions || []).filter((submission) =>
      submissionMatchesCustomerCareFilters(submission, normalized, now),
    ),
    normalized,
  );
};

export const hasDefaultCustomerCareDashboardSettings = (settings) => {
  const normalized = normalizeCustomerCareDashboardSettings(settings);
  return Object.keys(DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS).every(
    (key) => normalized[key] === DEFAULT_CUSTOMER_CARE_DASHBOARD_SETTINGS[key],
  );
};

export const buildCustomerCareExportRows = (submissions) =>
  (submissions || []).map((reg) => ({ ...reg, id: reg.id || '' }));
